/**
 * Real `VisibilityAPI` implementation backed by the HTTP client (ADR-0113).
 *
 *   getReport: GET /v1/sites/:site_id/visibility → VisibilityReport
 *
 * Read-only. The server computes the score + field presence from real data;
 * the bag just forwards the typed result. A 404 (cross-tenant/unknown site)
 * surfaces as the http client's mapped ApiError — the view treats it as an
 * empty/unavailable state.
 */

import type { SiteId, VisibilityReport } from '@sill/types';
import type { VisibilityAPI } from '@sill/types/api';

import type { HttpClient } from './http-client.js';

export interface RealVisibilityDeps {
  readonly http: HttpClient;
}

export function createRealVisibilityApi(deps: RealVisibilityDeps): VisibilityAPI {
  return {
    async getReport(site_id: SiteId): Promise<VisibilityReport> {
      const res = await deps.http.request<VisibilityReport>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/visibility`,
      });
      // The endpoint always returns a body on 200; an empty response means a
      // transport/route problem — surface it rather than return a fake report.
      if (res === undefined) {
        throw new Error('visibility_empty_response');
      }
      return res;
    },
  };
}
